import * as Sentry from '@sentry/react';
import { ClientError } from 'graphql-request';

interface GraphQLError {
  message: string;
  extensions?: {
    code?: string;
    path?: string;
    [key: string]: unknown;
  };
}

interface ErrorDetails {
  message: string;
  code?: string;
  path?: string;
  statusCode?: number;
  variables?: Record<string, unknown>;
}

export function formatError(error: unknown, context?: string): string {
  const details = extractErrorDetails(error);

  const parts: string[] = [];

  if (context) {
    parts.push(`[${context}]`);
  }

  parts.push(details.message);

  const diagnosticInfo: string[] = [];

  if (details.code) {
    diagnosticInfo.push(`Code: ${details.code}`);
  }

  if (details.path) {
    diagnosticInfo.push(`Path: ${details.path}`);
  }

  if (details.statusCode) {
    diagnosticInfo.push(`Status: ${details.statusCode}`);
  }

  if (details.variables && Object.keys(details.variables).length > 0) {
    diagnosticInfo.push(`Variables: ${JSON.stringify(details.variables)}`);
  }

  if (diagnosticInfo.length > 0) {
    parts.push(`(${diagnosticInfo.join(', ')})`);
  }

  return parts.join(' ');
}

export function extractErrorDetails(error: unknown): ErrorDetails {
  if (error instanceof ClientError) {
    const firstError = error.response?.errors?.[0] as GraphQLError | undefined;

    const details: ErrorDetails = {
      message: firstError?.message || error.message || 'Unknown GraphQL error',
      code: firstError?.extensions?.code,
      path: firstError?.extensions?.path,
      statusCode: error.response?.status,
    };

    if (error.request?.variables) {
      details.variables = error.request.variables as Record<string, unknown>;
    }

    return details;
  }

  if (error instanceof Error) {
    return {
      message: error.message,
    };
  }

  if (typeof error === 'string') {
    return {
      message: error,
    };
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return {
      message: String((error as { message: unknown }).message),
    };
  }

  return {
    message: `Unknown error: ${JSON.stringify(error)}`,
  };
}

const CREATE_USER_ERROR_MESSAGES: Record<string, string> = {
  emailExists: 'User with this email already exists',
  invalidEmail: 'Invalid email format',
  invalidPassword: 'Password must be at least 6 characters',
  requiredFields: 'Required fields are not filled',
};

interface ParsedAuthError {
  code?: string;
  message?: string;
}

function tryParseAuthErrorFromMessage(message: string): ParsedAuthError | null {
  const jsonPart = message.split(': ')[0]?.trim();
  if (!jsonPart?.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(jsonPart) as ParsedAuthError;
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

export function getCreateUserErrorMessage(error: unknown): string {
  const details = extractErrorDetails(error);
  const rawMessage = details.message;
  const code = details.code;

  const authParsed = tryParseAuthErrorFromMessage(rawMessage);

  if (
    authParsed?.code === 'auth/invalid-password' ||
    rawMessage.includes('auth/invalid-password')
  ) {
    return CREATE_USER_ERROR_MESSAGES.invalidPassword;
  }
  if (
    authParsed?.code === 'auth/invalid-email' ||
    rawMessage.includes('auth/invalid-email') ||
    code === 'validation-failed'
  ) {
    if (rawMessage.toLowerCase().includes('email')) {
      return CREATE_USER_ERROR_MESSAGES.invalidEmail;
    }
  }
  if (
    code === 'constraint-violation' ||
    rawMessage.includes('user_email_key') ||
    (rawMessage.includes('unique constraint') && rawMessage.toLowerCase().includes('email')) ||
    (rawMessage.includes('Uniqueness violation') && rawMessage.includes('user_email_key'))
  ) {
    return CREATE_USER_ERROR_MESSAGES.emailExists;
  }
  if (
    code === 'validation-error' ||
    rawMessage.toLowerCase().includes('required') ||
    rawMessage.toLowerCase().includes('not null')
  ) {
    return CREATE_USER_ERROR_MESSAGES.requiredFields;
  }
  if (
    rawMessage.toLowerCase().includes('invalid email') ||
    rawMessage.toLowerCase().includes('invalid format')
  ) {
    return CREATE_USER_ERROR_MESSAGES.invalidEmail;
  }
  if (rawMessage.includes('at least 6 characters') || rawMessage.includes('password must be')) {
    return CREATE_USER_ERROR_MESSAGES.invalidPassword;
  }

  return formatError(error, 'Create user');
}

function shouldReportToSentry(error: unknown): boolean {
  if (!Sentry.isInitialized()) {
    return false;
  }

  const details = extractErrorDetails(error);

  if (
    details.message.includes('Failed to fetch') ||
    details.message.includes('NetworkError') ||
    details.message.includes('timeout')
  ) {
    return false;
  }

  if (details.statusCode === 401 || details.statusCode === 403) {
    return false;
  }

  if (
    details.code &&
    (details.code.toLowerCase().includes('validation') ||
      details.code.toLowerCase().includes('permission-denied') ||
      details.code.toLowerCase().includes('access-denied'))
  ) {
    return false;
  }

  return true;
}

export function logError(error: unknown, context?: string): void {
  const details = extractErrorDetails(error);

  console.error('=== Error Details ===');
  if (context) {
    console.error('Context:', context);
  }
  console.error('Message:', details.message);

  if (details.code) {
    console.error('Code:', details.code);
  }

  if (details.path) {
    console.error('Path:', details.path);
  }

  if (details.statusCode) {
    console.error('Status Code:', details.statusCode);
  }

  if (details.variables) {
    console.error('Variables:', JSON.stringify(details.variables, null, 2));
  }

  console.error('Original error:', error);
  console.error('====================');

  if (shouldReportToSentry(error)) {
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      contexts: {
        graphql: {
          message: details.message,
          code: details.code,
          path: details.path,
          statusCode: details.statusCode,
          variables: details.variables,
        },
      },
      tags: {
        errorType: 'graphql',
        errorContext: context || 'unknown',
      },
      level: details.statusCode && details.statusCode >= 500 ? 'error' : 'warning',
    });
  }
}
